import React from 'react';
import {
    DefaultButton,
    Dropdown,
    IconButton,
    Panel,
    PanelType,
    Pivot,
    PivotItem,
    PrimaryButton,
    Stack,
    Text,
    TextField,
    Toggle,
} from '@fluentui/react';
import { Css } from 'nano-css/addon/vcssom/cssToTree';
import { IAffiliationItem, IAffiliations } from '../homePageConfig';
import { ISearchElementStyle, ISearchTextStyle } from '../searchPageConfig';
import StorefrontBannerImageUploader from '../Banners/StorefrontBannerImageUploader';
import { genId } from '../NavPods/StorefrontNavPodsEditor';
import { advancedReplacementPatch, AdvancedStyleButton, ElementPropertyRows, parseNum, StyleGroupAccordion } from '../styleControls';
import { StyleRow, StyleRowsWidthProvider, useCellControlStyles } from '../styleRows';
import { STYLE_PART_ICONS } from '../stylesConfig';
import AffiliationsExample from './AffiliationsExample';
import StorefrontSectionAdvancedStyleEditorModal from '../../StorefrontSectionAdvancedStyleEditorModal';
import Setting, { DynamicSetting } from '../../../Common/Setting';

const LABEL_POSITION_OPTIONS = [
    { key: 'top', text: 'Top' },
    { key: 'left', text: 'Left' },
    { key: 'bottom', text: 'Bottom' },
    { key: 'none', text: 'None' },
];
const POSITION_OPTIONS = [
    { key: 'top', text: 'Top of page' },
    { key: 'bottom', text: 'Bottom of page' },
];
const ALIGN_OPTIONS = [
    { key: 'left', text: 'Left' },
    { key: 'center', text: 'Center' },
    { key: 'right', text: 'Right' },
    { key: 'spaceBetween', text: 'Space between' },
];

// ISearchTextStyle is narrower than the ISearchElementStyle the generic rows speak, so the dicts
// don't structurally interchange in either direction even though at RUNTIME both are open camelCase
// CSS bags with an index signature. One loose cast at the boundary, in both directions (value in,
// patch out), same treatment as ProductCardStylePanel's.
const loose = (dict?: { [prop: string]: any }): any => dict;

// The 480px panel affords a wider control column than the 180px the flat rows default to (that
// default is tuned for the 340px style panels).
const CONTROL_WIDTH = 220;

// width/height ride in the style bag's index signature, so they need a cast to read back out.
const sizeValue = (dict: ISearchElementStyle | undefined, key: 'width' | 'height'): string =>
    (dict?.[key] as number | undefined)?.toString() ?? '';

// A single logo's editor row. Simpler than a nav pod row — no segment/category/manufacturer/
// tag/attribute filter, just the image, alt text, an optional link, and reordering/removal.
const AffiliationItemRow: React.FC<{
    item: IAffiliationItem;
    onChange: (item: IAffiliationItem) => void;
    onRemove: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
}> = (props) => {
    const { item } = props;
    return (
        <Stack horizontal tokens={{ childrenGap: '0.75em' }} verticalAlign='start' styles={{ root: { border: '1px solid #edebe9', borderRadius: 4, padding: '0.75em', background: '#fff' } }}>
            <Stack.Item>
                <Text variant='small' block styles={{ root: { marginBottom: 4 } }}>Logo</Text>
                <div style={{ width: 96, height: 96, border: '1px dashed #c8c6c4', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    <StorefrontBannerImageUploader
                        value={item.imageUrl}
                        onChange={(url) => props.onChange({ ...item, imageUrl: url })}
                        width={96}
                        height={96}
                        imageId={item.id}
                        isNew={!item.imageUrl}
                    />
                </div>
            </Stack.Item>
            <Stack.Item grow>
                <Stack tokens={{ childrenGap: '0.5em' }}>
                    <DynamicSetting id={`storefront.affiliationItem.${item.id}.altText`}>
                        <TextField
                            label='Alt Text'
                            value={item.altText ?? ''}
                            onChange={(_, v) => props.onChange({ ...item, altText: v || undefined })}
                            placeholder='e.g. Better Business Bureau Accredited'
                        />
                    </DynamicSetting>
                    <DynamicSetting id={`storefront.affiliationItem.${item.id}.linkUrl`}>
                        <TextField
                            label='Link URL'
                            value={item.linkUrl ?? ''}
                            onChange={(_, v) => props.onChange({ ...item, linkUrl: v || undefined })}
                            placeholder='https://partner.com (optional)'
                        />
                    </DynamicSetting>
                </Stack>
            </Stack.Item>
            <Stack.Item>
                <Stack>
                    <IconButton iconProps={{ iconName: 'ChevronUpSmall' }} ariaLabel='Move up' disabled={!props.onMoveUp} onClick={props.onMoveUp} styles={{ root: { height: 16 } }} />
                    <IconButton iconProps={{ iconName: 'ChevronDownSmall' }} ariaLabel='Move down' disabled={!props.onMoveDown} onClick={props.onMoveDown} styles={{ root: { height: 16 } }} />
                </Stack>
            </Stack.Item>
            <IconButton iconProps={{ iconName: 'Delete' }} ariaLabel='Remove logo' onClick={props.onRemove} />
        </Stack>
    );
};

// The band's styling in one surface, with the live example pinned above it. The groups are the
// Storefront Styles panel's part accordions, one open at a time, each ending in the same
// "Advanced…" escape into the raw key/value CSS editor. (No named-style picker: none of the
// affiliations keys maps to a style element type, so there is nothing to offer.)
const AffiliationsStyleTab: React.FC<{
    value: IAffiliations;
    onChange: (patch: Partial<IAffiliations>) => void;
    isDark?: boolean;
}> = ({ value, onChange, isDark }) => {
    const cell = useCellControlStyles();
    const updateBand = (patch: Partial<ISearchElementStyle>) => onChange({ band: { ...value.band, ...patch } });
    const updatePod = (patch: Partial<ISearchElementStyle>) => onChange({ pod: { ...value.pod, ...patch } });
    const updateImage = (patch: Partial<ISearchElementStyle>) => onChange({ image: { ...value.image, ...patch } });
    const updateLabel = (patch: Partial<ISearchTextStyle>) => onChange({ labelStyle: { ...value.labelStyle, ...patch } });
    const updateDescription = (patch: Partial<ISearchTextStyle>) => onChange({ descriptionStyle: { ...value.descriptionStyle, ...patch } });

    // One group open at a time, seeded on the band — the same accordion behaviour as the Storefront
    // Styles panel's part list.
    const [openGroup, setOpenGroup] = React.useState('band');
    const group = (key: string) => ({
        expanded: openGroup === key,
        onToggle: (expanded: boolean) => setOpenGroup(expanded ? key : ''),
    });

    // The raw key/value CSS editor, one entry per group. It hands back the WHOLE dict rather than a
    // patch (a key deleted there has to disappear), so each entry goes through
    // advancedReplacementPatch instead of merging.
    const [advancedGroup, setAdvancedGroup] = React.useState<string | undefined>();
    const advanced: Record<string, { value?: Record<string, any>; replace: (css: Css) => void }> = {
        band: { value: value.band, replace: (css) => updateBand(advancedReplacementPatch(value.band, css)) },
        pod: { value: value.pod, replace: (css) => updatePod(advancedReplacementPatch(value.pod, css)) },
        image: { value: value.image, replace: (css) => updateImage(advancedReplacementPatch(value.image, css)) },
        label: { value: value.labelStyle, replace: (css) => updateLabel(advancedReplacementPatch(value.labelStyle, css)) },
        description: { value: value.descriptionStyle, replace: (css) => updateDescription(advancedReplacementPatch(value.descriptionStyle, css)) },
    };

    return (
        <StyleRowsWidthProvider width={CONTROL_WIDTH}>
            {/* Pinned above the groups so every row's effect is on screen without scrolling back to
                the preview canvas (sf-card-example-sticky, StorefrontEditor.css). */}
            <div className='sf-card-example-sticky' style={{ marginTop: 8 }}>
                <AffiliationsExample value={value} isDark={isDark} />
            </div>

            <StyleGroupAccordion
                label='Band'
                iconName={STYLE_PART_ICONS.band}
                tooltip='The strip behind the logo row. Its background runs edge to edge on the live page; padding here sets how tall the strip is.'
                {...group('band')}
            >
                <ElementPropertyRows value={value.band} onChange={updateBand} settingKeyPrefix='storefront.affiliations.band' />
                <AdvancedStyleButton onClick={() => setAdvancedGroup('band')} />
            </StyleGroupAccordion>

            <StyleGroupAccordion
                label='Pod'
                iconName={STYLE_PART_ICONS.card}
                tooltip='The box around each individual logo. Unset leaves the logos bare on the band.'
                {...group('pod')}
            >
                <ElementPropertyRows value={value.pod} onChange={updatePod} settingKeyPrefix='storefront.affiliations.pod' />
                <AdvancedStyleButton onClick={() => setAdvancedGroup('pod')} />
            </StyleGroupAccordion>

            <StyleGroupAccordion
                label='Image'
                iconName={STYLE_PART_ICONS.image}
                tooltip='The logo images. Blank width and height keep the default 48px tall, scaled to fit.'
                {...group('image')}
            >
                <StyleRow label='Width'>
                    <TextField type='number' min={0} suffix='px' value={sizeValue(value.image, 'width')} onChange={(_, v) => updateImage({ width: parseNum(v) })} styles={cell.textField} />
                </StyleRow>
                <StyleRow label='Height'>
                    <TextField type='number' min={0} suffix='px' value={sizeValue(value.image, 'height')} onChange={(_, v) => updateImage({ height: parseNum(v) })} styles={cell.textField} />
                </StyleRow>
                <ElementPropertyRows value={value.image} onChange={updateImage} settingKeyPrefix='storefront.affiliations.image' />
                <AdvancedStyleButton onClick={() => setAdvancedGroup('image')} />
            </StyleGroupAccordion>

            <StyleGroupAccordion
                label='Label'
                iconName={STYLE_PART_ICONS.title}
                tooltip='The band heading set above (or beside) the logos.'
                {...group('label')}
            >
                <ElementPropertyRows value={loose(value.labelStyle)} onChange={(patch) => updateLabel(loose(patch))} settingKeyPrefix='storefront.affiliations.label' />
                <AdvancedStyleButton onClick={() => setAdvancedGroup('label')} />
            </StyleGroupAccordion>

            <StyleGroupAccordion
                label='Description'
                iconName={STYLE_PART_ICONS.body}
                tooltip='The optional blurb under the label. Renders only when a description is written on the Logos tab.'
                {...group('description')}
            >
                <ElementPropertyRows value={loose(value.descriptionStyle)} onChange={(patch) => updateDescription(loose(patch))} settingKeyPrefix='storefront.affiliations.description' />
                <AdvancedStyleButton onClick={() => setAdvancedGroup('description')} />
            </StyleGroupAccordion>

            {/* Rendered once, outside the accordions: the raw CSS editor for whichever group asked. */}
            {advancedGroup && advanced[advancedGroup] && (
                <StorefrontSectionAdvancedStyleEditorModal
                    css={(advanced[advancedGroup].value ?? {}) as Css}
                    displayModal
                    closer={() => setAdvancedGroup(undefined)}
                    onChange={(css) => advanced[advancedGroup].replace(css)}
                />
            )}
        </StyleRowsWidthProvider>
    );
};

interface IStorefrontAffiliationsEditorProps {
    isOpen: boolean;
    onDismiss: () => void;
    storefrontId: string;
    value?: IAffiliations;
    onChange: (next: IAffiliations) => void;
    // Deep link from the home page canvas: open the panel on this tab. One-shot: the parent clears
    // it via onInitialPivotHandled as soon as it's consumed, so the same entry point re-triggers.
    initialPivot?: 'logos' | 'style';
    onInitialPivotHandled?: () => void;
    // The storefront's background is dark, so the band example switches to its dark variant.
    isDark?: boolean;
}

// Management panel for the Affiliations / Endorsements / Awards band: band-level label/position/
// alignment controls up top, then one row per logo. Mirrors StorefrontNavPodsEditor's structure
// (add/remove/move pattern, same image uploader) but without any filter-destination fields.
const StorefrontAffiliationsEditor: React.FC<IStorefrontAffiliationsEditorProps> = (props) => {
    const value = props.value ?? {};
    const items = value.items ?? [];

    const update = (patch: Partial<IAffiliations>) => props.onChange({ ...value, ...patch });

    const updateItem = (index: number, item: IAffiliationItem) => {
        const next = [...items];
        next[index] = item;
        update({ items: next });
    };
    const removeItem = (index: number) => {
        update({ items: items.filter((_, i) => i !== index) });
    };
    const moveItem = (index: number, dir: -1 | 1) => {
        const target = index + dir;
        if (target < 0 || target >= items.length) return;
        const next = [...items];
        [next[index], next[target]] = [next[target], next[index]];
        update({ items: next });
    };
    const addItem = () => {
        update({ items: [...items, { id: genId() }] });
    };

    // Which tab is showing. Local (not derived from the prop) so the merchant's own tab clicks
    // stick; the deep link below only seeds it.
    const [pivot, setPivot] = React.useState<'logos' | 'style'>('logos');
    // Deep link from the canvas (the band's edit pencil opens the Style tab). Consumed immediately
    // so the parent's one-shot state resets and clicking the same pencil again re-fires it.
    React.useEffect(() => {
        if (!props.isOpen || !props.initialPivot) return;
        setPivot(props.initialPivot);
        props.onInitialPivotHandled?.();
    }, [props.isOpen, props.initialPivot]);

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            headerText='Affiliations / Endorsements / Awards'
            type={PanelType.custom}
            customWidth='480px'
            isBlocking={false}
            closeButtonAriaLabel='Close'
        >
            <Pivot
                style={{ marginTop: 8 }}
                selectedKey={pivot}
                onLinkClick={(item) => setPivot((item?.props.itemKey as 'logos' | 'style') ?? 'logos')}
            >
                <PivotItem itemKey='logos' headerText='Logos'>
                    <Stack tokens={{ childrenGap: '0.75em' }} style={{ marginTop: 8 }}>
                        <Text variant='small' styles={{ root: { color: '#605e5c' } }}>
                            A row of partner/affiliation/award logos with an optional label, shown at the top or
                            bottom of the home page.
                        </Text>
                        <Setting id='storefront.affiliations.label'>
                            <TextField
                                label='Label'
                                value={value.label ?? ''}
                                onChange={(_, v) => update({ label: v || undefined })}
                                placeholder='e.g. As Seen In / Proud Member Of'
                            />
                        </Setting>
                        <Setting id='storefront.affiliations.description'>
                        <TextField
                            label='Description'
                            multiline
                            autoAdjustHeight
                            value={value.description ?? ''}
                            onChange={(_, v) => update({ description: v || undefined })}
                            placeholder='Optional blurb shown under the title'
                        />
                        </Setting>
                        <Stack horizontal wrap tokens={{ childrenGap: '0.75em' }}>
                            <Setting id='storefront.affiliations.labelPosition'>
                                <Dropdown
                                    label='Label Position'
                                    options={LABEL_POSITION_OPTIONS}
                                    selectedKey={value.labelPosition ?? 'top'}
                                    onChange={(_, o) => update({ labelPosition: (o?.key as IAffiliations['labelPosition']) || undefined })}
                                    styles={{ root: { width: 150 } }}
                                />
                            </Setting>
                            <Setting id='storefront.affiliations.pagePosition'>
                                <Dropdown
                                    label='Page Position'
                                    options={POSITION_OPTIONS}
                                    selectedKey={value.position ?? 'bottom'}
                                    onChange={(_, o) => update({ position: (o?.key as IAffiliations['position']) || undefined })}
                                    styles={{ root: { width: 150 } }}
                                />
                            </Setting>
                            <Setting id='storefront.affiliations.alignment'>
                                <Dropdown
                                    label='Alignment'
                                    options={ALIGN_OPTIONS}
                                    selectedKey={value.align ?? 'center'}
                                    onChange={(_, o) => update({ align: (o?.key as IAffiliations['align']) || undefined })}
                                    styles={{ root: { width: 150 } }}
                                />
                            </Setting>
                            <Setting id='storefront.affiliations.gap'>
                                <TextField
                                    label='Gap (px)'
                                    type='number'
                                    min={0}
                                    value={value.gap?.toString() ?? ''}
                                    onChange={(_, v) => { const n = parseInt(v ?? '', 10); update({ gap: isNaN(n) ? undefined : n }); }}
                                    placeholder='24'
                                    styles={{ root: { width: 100 } }}
                                />
                            </Setting>
                            <Setting id='storefront.affiliations.scroll'>
                                <Toggle
                                    label='Scroll (marquee)'
                                    checked={!!value.scroll}
                                    onText='On'
                                    offText='Off'
                                    onChange={(_, checked) => update({ scroll: checked || undefined })}
                                    styles={{ root: { width: 130 } }}
                                />
                            </Setting>
                            {value.scroll && (
                                <TextField
                                    label='Scroll speed (s)'
                                    type='number'
                                    min={5}
                                    value={value.scrollSpeed?.toString() ?? ''}
                                    onChange={(_, v) => { const n = parseInt(v ?? '', 10); update({ scrollSpeed: isNaN(n) ? undefined : n }); }}
                                    placeholder='30'
                                    styles={{ root: { width: 120 } }}
                                />
                            )}
                        </Stack>
                        {items.length === 0 && (
                            <Text styles={{ root: { color: '#605e5c' } }}>No logos yet. Add one to get started.</Text>
                        )}
                        {items.map((item, index) => (
                            <AffiliationItemRow
                                key={item.id}
                                item={item}
                                onChange={(i) => updateItem(index, i)}
                                onRemove={() => removeItem(index)}
                                onMoveUp={index > 0 ? () => moveItem(index, -1) : undefined}
                                onMoveDown={index < items.length - 1 ? () => moveItem(index, 1) : undefined}
                            />
                        ))}
                        <div>
                            <PrimaryButton iconProps={{ iconName: 'Add' }} text='Add Logo' onClick={addItem} />
                        </div>
                        <DefaultButton text='Done' onClick={props.onDismiss} />
                    </Stack>
                </PivotItem>
                <PivotItem itemKey='style' headerText='Style'>
                    <AffiliationsStyleTab value={value} onChange={update} isDark={props.isDark} />
                </PivotItem>
            </Pivot>
        </Panel>
    );
};

export default StorefrontAffiliationsEditor;
